import {
  formatDistanceToNow,
  formatDistanceToNowStrict,
  isFuture,
} from 'date-fns';
import React, { useState } from 'react';
import * as styles from './AcceptInvitation.module.scss';
import {
  Button,
  Heading,
  Icon,
  IconList,
  Modal,
  Strong,
  Text,
} from 'components/core';

import { Inline, Stack } from 'components/layout';

export type AcceptInvitationProps = {
  acceptUrl: string;
  declineUrl: string;
  signedIn: boolean;
  inviteTitle: string;
  inviteType: string;
  canBeFoundIn: string;
  sender: string;
  createdAt: string;
  expiresAt: string;
};

const expiresText = (expiresAt: string) => {
  const date = new Date(expiresAt);

  if (isFuture(date)) {
    return `Expires in ${formatDistanceToNowStrict(date)}`;
  }

  return `Expired ${formatDistanceToNow(date, { addSuffix: true })}`;
};

const CloseButton = (props: { onClose?: () => any }) => {
  return (
    <Button
      iconOnly
      type="neutral"
      variant="ghost"
      size="sm"
      onClick={() => props.onClose?.()}
      aria-label="Close"
    >
      <Icon icon={IconList.light.faClose} size="lg" />
    </Button>
  );
};

export const AcceptInvitation = ({
  inviteTitle,
  inviteType,
  sender,
  acceptUrl,
  declineUrl,
  createdAt,
  expiresAt,
}: AcceptInvitationProps) => {
  const [showModal, setShowModal] = useState<boolean>(true);

  return (
    <Modal isOpen={showModal}>
      <Inline alignX="spaceBetween">
        <Heading type="h4">{"You've been invited to join a review"}</Heading>
        <CloseButton
          onClose={() => {
            setShowModal(false);
          }}
        />
      </Inline>
      <Stack spacing={5} className={styles.container}>
        <Text>
          <Strong>{sender}</Strong> has invited you to join the{' '}
          {`${inviteType} `}
          <Strong>&quot;{inviteTitle}&quot;</Strong>
        </Text>
        <Text>
          Once you accept, you’ll see this review in{' '}
          <Strong>Your Reviews.</Strong>
        </Text>
      </Stack>
      <Text variant="weak" className={styles.time}>
        <span className="hide-in-percy">
          Sent {formatDistanceToNow(new Date(createdAt), { addSuffix: true })} ·{' '}
          {expiresText(expiresAt)}
        </span>
      </Text>
      <Inline alignX="right" spacing={2}>
        <Button type="neutral" variant="outline" to={declineUrl}>
          Decline
        </Button>
        <Button type="brand" to={acceptUrl}>
          Accept invite
        </Button>
      </Inline>
    </Modal>
  );
};
